import React, { useEffect, useRef, useState } from "react";
import "./index.less";
import { Button, Input, InputRef, Popover, Space, Upload } from "antd";
import { CloseOutlined } from "@ant-design/icons";
import { useTranslate } from "@obsbot/remo-i18n";
import { useChannelRoomStore, useChatStore } from "@obsbot/remo-stores";
import { CustomType, ICustomPayloadDescription, PeerMsgType } from "@obsbot/remo-services";
import { useParams } from "react-router-dom";
import { IMsgItem } from "../ChatMsg";
// 引入 emoji-mart 相关的组件和数据
import data, { Emoji, Skin } from "@emoji-mart/data";
import Picker from "@emoji-mart/react";
import { AttachmentIcon, EmojiIcon } from "../../SvgIconCom/PrivateChatTopIcon";
import { eventBus } from "@obsbot/remo-utils";
import { REMO_GAME_WEB_URL } from "@obsbot/remo-server-api";
export enum ChatType {
    Chat = "Chat",
    History = "History",
    Channel = "Channel",
}

export interface ChatSendInputProps {
    hoverWindow?: boolean;
    handleSendMessage?: (msg: string, msgType: CustomType, success: boolean) => void;
    handleTempMsg?: (msg: File, type: CustomType) => void;
    referenceMessage: IMsgItem | null;
    setReferenceMessage: (message: IMsgItem | null) => void;
    chatType?: ChatType;
}
export const ChatSendInput: React.FC<ChatSendInputProps> = ({
    hoverWindow = true,
    handleSendMessage,
    handleTempMsg,
    referenceMessage,
    setReferenceMessage,
    chatType = ChatType.Chat,
}) => {
    const { id = "", roomUUID = "" } = useParams();
    const { rtm } = useChatStore();
    const t = useTranslate();
    const [draftMsg, setDraftMsg] = useState<string>("");
    const uploadRef = useRef<any>(null);
    const [cantSendMsg, setCantSendMsg] = useState<boolean>(false);
    const { blockedUsers } = useChannelRoomStore();
    const inputRef = useRef<InputRef>(null);

    const sendMessage = async (): Promise<void> => {
        // 发送消息
        console.log("[DEBUG LOG] 触发发送事件, 查看判断", rtm, draftMsg, id, roomUUID);
        if (!rtm || !draftMsg || (!id && !roomUUID)) {
            return;
        }
        let message;
        let messageType = CustomType.Text;
        if (chatType === ChatType.Chat) {
            let payload: PeerMsgType;
            if (referenceMessage) {
                payload = {
                    data: draftMsg,
                    description: ICustomPayloadDescription.ChatReference,
                    extension: JSON.stringify(referenceMessage),
                };
                messageType = CustomType.Custom;
                // message = await rtm.sendPeerMessage(CustomType.Custom, id, payload);
            } else if (draftMsg.startsWith(`${REMO_GAME_WEB_URL}#/channel/invite/`)) {
                messageType = CustomType.Custom;
                payload = {
                    data: draftMsg,
                    description: ICustomPayloadDescription.Link,
                };
            } else {
                payload = {
                    text: draftMsg,
                };
            }
            message = await rtm.sendPeerMessage(messageType, id, payload);
            eventBus.emit("send-new-msg", { userID: id, conversationID: "C2C" + id });
        } else {
            console.log("[DEBUG LOG] 向群组发送消息", draftMsg);
            message = await rtm.sendRoomMessage(CustomType.Text, {
                text: draftMsg,
            });
            console.log("[DEBUG LOG] 发送结束", message);
        }
        setReferenceMessage(null);
        handleSendMessage && handleSendMessage(draftMsg, messageType, Boolean(message));
        message !== "" && setDraftMsg("");
    };

    // 切换会话时清空引用消息及草稿
    useEffect(() => {
        setDraftMsg("");
        setReferenceMessage(null);
        if (!hoverWindow) {
            inputRef.current?.focus();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    useEffect(() => {
        if (!hoverWindow) {
            inputRef.current?.focus();
        }
    }, [referenceMessage]);

    useEffect(() => {
        if (chatType === ChatType.Chat) {
            if (blockedUsers.includes(id)) {
                setCantSendMsg(true);
            } else {
                setCantSendMsg(false);
            }
        }
    }, [blockedUsers, id, chatType]);

    const sendFileMsg = async (options: any): Promise<void> => {
        if (!rtm) {
            return;
        }
        const curType = options.file.type.split("/")[0];
        const sendType = getFileMsgType(curType);
        handleTempMsg && handleTempMsg(options.file, sendType);
        // 发送媒体文件(音频、视频)需配置cloudCustomData, 便于接收端可以得到文件名称
        const message = await rtm.sendPeerMessage(sendType, id, {
            file: options.file,
            cloudCustomData: options.file.name,
        });
        handleSendMessage && handleSendMessage("", sendType, Boolean(message));
    };

    const getFileMsgType = (fileType: string): CustomType => {
        switch (fileType) {
            case "image": {
                return CustomType.Image;
            }
            case "video": {
                return CustomType.Video;
            }
            case "audio": {
                return CustomType.Audio;
            }
            default: {
                return CustomType.File;
            }
        }
    };

    const selectEmoji = (emoji: Emoji & Skin): void => {
        setDraftMsg(draftMsg + emoji.native);
    };

    const getReferenceRender = (): string => {
        const payload = referenceMessage?.payload;
        switch (referenceMessage?.type) {
            case "TIMTextElem": {
                return payload.text;
            }
            case "TIMImageElem": {
                return `[${t("chat.img")}]`;
            }
            case "TIMVideoFileElem": {
                return `[${t("chat.video")}]`;
            }
            case "TIMFileElem": {
                return `[${t("chat.file")}]`;
            }
            case "TIMCustomElem": {
                switch (payload.description) {
                    case ICustomPayloadDescription.ChatReference: {
                        return `[${t("chat.reference")}]: ${payload.data}`;
                    }
                    case ICustomPayloadDescription.IntimateRelation: {
                        return `[${t("chat.relationship")}${t("chat.msg")}]`;
                    }
                    case ICustomPayloadDescription.Link: {
                        return `[${t("inviteFriends")}]: ${payload.data}`;
                    }
                    default: {
                        return t("chat.unknown-msg-type");
                    }
                }
            }
            default: {
                return t("chat.unknown-msg-type");
            }
        }
    };
    return (
        <div className={`chat-send-input ${cantSendMsg ? "disabled" : ""}`}>
            {referenceMessage && (
                <div className="chat-reference-message">
                    <div className="message-content">
                        <div>{referenceMessage.nick || referenceMessage.from} </div>
                        <div className="content-detail"> {getReferenceRender()}</div>
                    </div>
                    <div className="close" onClick={() => setReferenceMessage(null)}>
                        <CloseOutlined
                            onPointerMoveCapture={undefined}
                            onPointerOverCapture={undefined}
                        />
                    </div>
                </div>
            )}
            <Space style={{ width: "100%" }}>
                <Space.Compact
                    className={`send-input-compact ${hoverWindow ? "hover-window-input" : "chat-window-input"}`}
                >
                    {hoverWindow && (
                        <Popover
                            classNames={{
                                root: "chat-emoji-popover",
                            }}
                            content={
                                <Picker data={data} theme="dark" onEmojiSelect={selectEmoji} />
                            }
                            placement="top"
                            trigger="click"
                        >
                            <Button
                                disabled={cantSendMsg || (!id && !roomUUID)}
                                icon={<EmojiIcon />}
                                size="large"
                                style={{ fontSize: 20 }}
                                type="text"
                            />
                        </Popover>
                    )}
                    <Input
                        ref={inputRef}
                        className="send-input"
                        disabled={cantSendMsg || (!id && !roomUUID)}
                        placeholder={t("chat.input-enter" + (cantSendMsg ? "-disabled" : ""))}
                        value={draftMsg}
                        onChange={e => setDraftMsg(e.target.value)}
                        onPressEnter={sendMessage}
                    />
                    {!hoverWindow && (
                        <>
                            <Popover
                                content={
                                    <Picker data={data} theme="dark" onEmojiSelect={selectEmoji} />
                                }
                                placement="top"
                                trigger="click"
                            >
                                <Button
                                    disabled={cantSendMsg}
                                    icon={<EmojiIcon />}
                                    style={{ fontSize: 20 }}
                                    type="text"
                                ></Button>
                            </Popover>
                            <Upload
                                ref={uploadRef}
                                accept=".jpg,.jpeg,.png,.gif,.webp,.mp4,.mov"
                                customRequest={sendFileMsg}
                                disabled={cantSendMsg}
                                showUploadList={false}
                            >
                                <Button
                                    disabled={cantSendMsg}
                                    icon={<AttachmentIcon />}
                                    style={{ fontSize: 20 }}
                                    type="text"
                                ></Button>
                            </Upload>
                            <Button
                                className="sent-btn"
                                disabled={cantSendMsg}
                                type="primary"
                                onClick={sendMessage}
                            >
                                {t("chat.send")}
                            </Button>
                        </>
                    )}
                </Space.Compact>
            </Space>
        </div>
    );
};
